/**
 * Pestaña 2 — Asistencia al Viaje (Módulo TIME).
 *
 * Flujo de la reunión del 10-09: de los grupos MODULO TIME, los que fichan
 * entrada; con viaje → licencia ASISTENCIA AL VIAJE, sin viaje → licencia
 * SIN TAREA; en los dos casos, salida al fin del turno.
 *
 * Copy (§11): el botón dice qué pasa y el resultado usa el mismo verbo
 * ("Procesar" → "Procesada").
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Search,
  Download,
  ListFilter,
  Loader2,
  ClipboardList,
  History,
} from 'lucide-react';

import {
  fueCancelada,
  mensajeDe,
  type DecisionSupervisor,
  type JornadaDto,
  type Modo,
} from '../../api';
import { Aviso, Avisos } from '../../components/Aviso';
import { CampoFecha } from '../../components/CampoFecha';
import { ModalCargando } from '../../components/ModalCargando';
import { mensaje, type Mensaje } from '../../components/mensajes';
import { useSesion } from '../../components/useSesion';
import { etiquetaAccion } from '../../lib/etiquetas';
import { aDdMmAaaa, fechaHoy } from '../../lib/fechas';
import { Historico } from './Historico';
import { DetalleJornada } from './DetalleJornada';
import { DialogoMasivo } from './DialogoMasivo';
import { DialogoProcesar, type ResultadoProceso } from './DialogoProcesar';
import { TablaJornadas } from './TablaJornadas';
import { claveDe, PENDIENTES } from './dominio';
import {
  catalogos,
  descargarJornadasXlsx,
  impactarMasivo,
  listarJornadas,
  procesarJornada,
  resolverJornada,
  type FiltroJornadas,
} from './api';

const ESTADOS = [
  ['', 'Todos'],
  ['ASISTENCIA_AL_VIAJE', 'Asistencia al viaje'],
  ['SIN_TAREA', 'Sin tarea'],
  ['ESPERANDO_VIAJE', 'Esperando viaje'],
  ['IMPACTADA', 'Impactada'],
  ['SIN_TURNO_CONFIGURADO', 'Sin turno configurado'],
  ['VIAJE_SIN_FICHADA', 'Viaje sin fichada']
] as const;

/** Sub-solapas de la pestaña. */
type SubSolapa = 'jornadas' | 'historico';

/**
 * El rango consultado, como se lee: `16/09 al 17/09/2026`.
 *
 * Con los dos días en el mismo año el año se dice una sola vez, al final: es
 * la forma en que se dice un rango corto, que es el caso de todos los días.
 */
function rangoLegible(desde: string, hasta: string): string {
  const d = aDdMmAaaa(desde);
  const h = aDdMmAaaa(hasta);
  if (d === h) return d;
  return d.slice(6) === h.slice(6) ? `${d.slice(0, 5)} al ${h}` : `${d} al ${h}`;
}

/**
 * La pestaña.
 *
 * El ingreso y el cambio de clave obligatorio ya NO están acá: desde el 15-09
 * son del portal entero (`App.tsx`), porque hay tres solapas y el login vivía
 * dentro de una sola. Acá solo queda el caso de que el módulo no esté
 * configurado.
 */
export function VistaAsistencia(): React.JSX.Element {
  const { modulo, cargando } = useSesion();

  if (cargando) {
    return (
      <div className="asistencia cargando">
        <Loader2 className="girando" size={20} aria-hidden="true" />
        Cargando el módulo…
      </div>
    );
  }

  if (!modulo?.habilitado) {
    return (
      <div className="asistencia">
        <section className="bloqueo">
          <h2>La pestaña no está habilitada</h2>
          <p>{modulo?.motivo ?? 'No se pudo determinar el estado del módulo.'}</p>
          <p className="nota">
            Se configura con variables de entorno. Ver <code>.env.example</code>.
          </p>
        </section>
      </div>
    );
  }

  return <Consola />;
}

function Consola(): React.JSX.Element {
  const { modulo } = useSesion();

  // Mismo rango que la corrida diaria: ayer + hoy (decisión del 11-09). El día
  // anterior se reevalúa porque Cristal solo informa viajes cerrados.
  const [fechaDesde, setFechaDesde] = useState(() => fechaHoy(-1));
  const [fechaHasta, setFechaHasta] = useState(() => fechaHoy());
  const [dni, setDni] = useState('');
  const [estado, setEstado] = useState('');
  const [grupo, setGrupo] = useState('');
  const [empresa, setEmpresa] = useState('');

  const [jornadas, setJornadas] = useState<readonly JornadaDto[]>([]);
  /**
   * El rango de la consulta que está EN PANTALLA, que no es el de los campos:
   * quien cambia las fechas y todavía no busca sigue viendo el rango anterior,
   * y la bajada tiene que describir lo que se está mirando.
   */
  const [rangoMostrado, setRangoMostrado] = useState(() => rangoLegible(fechaHoy(-1), fechaHoy()));
  const [grupos, setGrupos] = useState<readonly string[]>([]);
  const [empresas, setEmpresas] = useState<readonly string[]>([]);
  const [seleccion, setSeleccion] = useState<ReadonlySet<string>>(new Set());
  const [activa, setActiva] = useState<string | null>(null);
  const [avisos, setAvisos] = useState<readonly Mensaje[]>([]);
  const [buscando, setBuscando] = useState(false);
  /**
   * Corte de la consulta en curso.
   *
   * En un ref y no en el estado: cambiarlo no tiene que redibujar nada, y
   * `buscar` necesita leer el valor vigente y no el de la última renderización.
   */
  const corte = useRef<AbortController | null>(null);
  const [procesando, setProcesando] = useState<string | null>(null);
  const [resolviendo, setResolviendo] = useState(false);
  const [dialogoAbierto, setDialogoAbierto] = useState(false);
  /** Jornada esperando confirmación para procesarse. */
  const [aConfirmar, setAConfirmar] = useState<JornadaDto | null>(null);
  /**
   * Si la grilla está mostrando solo lo pendiente.
   *
   * Ese filtro se aplica del lado del navegador, así que hay que recordarlo
   * para que la descarga baje lo mismo que está a la vista.
   */
  const [soloPendientes, setSoloPendientes] = useState(false);
  const [descargando, setDescargando] = useState(false);
  /**
   * Cómo salió lo que se confirmó.
   *
   * El diálogo se queda abierto hasta que el operador lo cierra: mostrarle el
   * resultado ahí mismo es la única forma de que sepa si su registro entró,
   * sin tener que ir a buscarlo entre los avisos.
   */
  const [resultadoProceso, setResultadoProceso] = useState<ResultadoProceso | null>(null);
  const [solapa, setSolapa] = useState<SubSolapa>('jornadas');
  /** Sub-solapas ya abiertas: quedan montadas para conservar su estado. */
  const [visitadas, setVisitadas] = useState<ReadonlySet<SubSolapa>>(
    () => new Set<SubSolapa>(['jornadas']),
  );

  const irA = useCallback((s: SubSolapa) => {
    setSolapa(s);
    setVisitadas((previas) => (previas.has(s) ? previas : new Set([...previas, s])));
  }, []);
  const [modo, setModo] = useState<Modo>(modulo?.modo ?? 'demo');

  const filtroActual = useMemo<FiltroJornadas>(
    () => ({
      fechaDesde,
      fechaHasta,
      ...(dni.trim() ? { dni: dni.trim() } : {}),
      ...(estado ? { estado } : {}),
      ...(grupo ? { grupo } : {}),
      ...(empresa ? { empresa } : {}),
    }),
    [fechaDesde, fechaHasta, dni, estado, grupo, empresa],
  );

  const buscar = useCallback(
    async (opciones: { soloPendientes?: boolean; conservarAvisos?: boolean } = {}) => {
      // Una consulta nueva cancela la anterior: si alguien vuelve a buscar
      // mientras la primera sigue corriendo, dos consultas en paralelo solo
      // gastan cuota de GeoVictoria y la que llegue última pisa a la otra.
      corte.current?.abort();
      const propio = new AbortController();
      corte.current = propio;

      setBuscando(true);
      setActiva(null);
      try {
        const datos = await listarJornadas(
          opciones.soloPendientes ? { ...filtroActual, estado: '' } : filtroActual,
          propio.signal,
        );
        const filtradas = opciones.soloPendientes
          ? datos.jornadas.filter((j) => PENDIENTES.includes(j.estado))
          : datos.jornadas;

        setJornadas(filtradas);
        setRangoMostrado(rangoLegible(fechaDesde, fechaHasta));
        setSoloPendientes(opciones.soloPendientes === true);
        setSeleccion(new Set());
        setModo(datos.modo);
        // Vienen con el listado y se calculan antes del filtro de empresa:
        // así el desplegable no se queda con una sola opción al usarlo.
        setEmpresas(datos.empresas);
        if (!opciones.conservarAvisos) {
          setAvisos(datos.avisos.map((a) => mensaje('info', a)));
        }

        try {
          setGrupos((await catalogos()).grupos);
        } catch {
          /* los filtros son opcionales: si fallan, se dejan como estaban */
        }
      } catch (err) {
        // Cancelar no es un error: se deja lo que había en pantalla y se avisa
        // en tono neutro. Pintarlo de rojo haría pensar que algo se rompió.
        if (fueCancelada(err)) {
          setAvisos([mensaje('info', 'Consulta cancelada. Se muestra el último resultado.')]);
        } else {
          setJornadas([]);
          setAvisos([mensaje('error', mensajeDe(err))]);
        }
      } finally {
        // Si ya arrancó otra consulta, el `finally` de la vieja no tiene que
        // apagar el cartel de la nueva.
        if (corte.current === propio) {
          corte.current = null;
          setBuscando(false);
        }
      }
    },
    [filtroActual, fechaDesde, fechaHasta],
  );

  /** Corta la consulta en curso. El `catch` de arriba se encarga del resto. */
  const cancelarBusqueda = useCallback(() => {
    corte.current?.abort();
  }, []);

  // Primera carga. Después la búsqueda es siempre explícita: no se dispara
  // sola al tipear, porque cada consulta pega contra GeoVictoria y los TMS.
  useEffect(() => {
    void buscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const jornadaActiva = jornadas.find((j) => claveDe(j) === activa) ?? null;
  const seleccionadas = jornadas.filter((j) => seleccion.has(claveDe(j)));
  /*
   * Cuántas operaciones va a generar la selección. No es la cantidad de filas:
   * una jornada puede registrar la licencia y además marcar la salida, y decir
   * "2 jornadas" antes de escribir en GeoVictoria oculta la mitad de lo que
   * se va a hacer.
   */
  const operaciones = seleccionadas.reduce((n, j) => n + j.etiquetasAcciones.length, 0);

  function alternar(clave: string): void {
    setSeleccion((previa) => {
      const proxima = new Set(previa);
      if (proxima.has(clave)) proxima.delete(clave);
      else proxima.add(clave);
      return proxima;
    });
  }

  function alternarTodas(marcar: boolean): void {
    setSeleccion(
      marcar ? new Set(jornadas.filter((j) => j.admiteImpacto).map(claveDe)) : new Set(),
    );
  }

  /**
   * Baja la consulta en Excel.
   *
   * Se descarga lo mismo que está en pantalla, con los filtros puestos: una
   * planilla que no coincide con lo que se ve es peor que no tenerla.
   */
  async function descargar(): Promise<void> {
    setDescargando(true);
    try {
      const { blob, nombre } = await descargarJornadasXlsx({ ...filtroActual, soloPendientes });

      // El navegador no descarga un Blob sin un enlace: se arma uno, se
      // dispara y se suelta la URL, que si no queda retenida en memoria.
      const url = URL.createObjectURL(blob);
      const enlace = document.createElement('a');
      enlace.href = url;
      enlace.download = nombre;
      document.body.appendChild(enlace);
      enlace.click();
      enlace.remove();
      URL.revokeObjectURL(url);

      setAvisos([mensaje('ok', `Se descargó ${nombre} con ${String(jornadas.length)} jornada(s).`)]);
    } catch (err) {
      setAvisos([mensaje('error', `No se pudo descargar la planilla: ${mensajeDe(err)}`)]);
    } finally {
      setDescargando(false);
    }
  }

  async function procesar(j: JornadaDto): Promise<void> {
    setProcesando(claveDe(j));
    try {
      const { resultados, jornada } = await procesarJornada(j.dni, j.fechaOperativa);
      const ok = resultados.filter((r) => r.resultado === 'OK' && !r.yaEstaba);
      const previos = resultados.filter((r) => r.yaEstaba);
      const fallidos = resultados.filter((r) => r.resultado !== 'OK');

      const partes: string[] = [];
      if (ok.length > 0) partes.push(`registrado: ${ok.map((r) => r.tipoImpacto).join(', ')}`);
      if (previos.length > 0) {
        partes.push(`ya estaba: ${previos.map((r) => r.tipoImpacto).join(', ')}`);
      }
      if (fallidos.length > 0) {
        partes.push(
          `rechazado: ${fallidos
            .map((r) => `${r.tipoImpacto} (${r.detalle ?? 'sin detalle'})`)
            .join(', ')}`,
        );
      }

      /*
       * Se reemplaza SOLO la fila procesada, con la jornada que devolvió el
       * back ya reevaluada. Volver a buscar acá rehacía la consulta del día
       * entera —medio minuto— para actualizar un renglón.
       */
      setJornadas((previas) =>
        previas.map((p) => (claveDe(p) === claveDe(jornada) ? jornada : p)),
      );
      setResultadoProceso({
        huboError: fallidos.length > 0,
        lineas: resultados.map((r) => ({
          texto:
            r.resultado !== 'OK'
              ? `${etiquetaAccion(r.tipoImpacto)} — rechazado: ${r.detalle ?? 'sin detalle'}`
              : r.yaEstaba
                ? `${etiquetaAccion(r.tipoImpacto)} — ya estaba registrada`
                : `${etiquetaAccion(r.tipoImpacto)} — registrada`,
          estado: r.resultado !== 'OK' ? 'error' : r.yaEstaba ? 'yaEstaba' : 'ok',
        })),
      });
      setAvisos([
        mensaje(
          fallidos.length > 0 ? 'error' : 'ok',
          `Procesada: dni ${j.dni}, ${aDdMmAaaa(j.fechaOperativa)} — ${partes.join(' · ')}.`,
        ),
      ]);
    } catch (err) {
      // El error también se muestra en el diálogo: es donde está mirando.
      setResultadoProceso({
        huboError: true,
        lineas: [{ texto: mensajeDe(err), estado: 'error' }],
      });
      setAvisos([mensaje('error', mensajeDe(err))]);
    } finally {
      setProcesando(null);
    }
  }

  async function confirmarMasivo(): Promise<void> {
    setDialogoAbierto(false);
    const items = seleccionadas.map((j) => ({ dni: j.dni, fecha: j.fechaOperativa }));
    if (items.length === 0) return;

    setAvisos([mensaje('info', 'Procesando…')]);
    try {
      const r = await impactarMasivo(items);
      // §8.4 punto 3: reporte discriminado, no un "listo" genérico.
      const nodos: Mensaje[] = [
        mensaje(
          'ok',
          `Registros hechos: ${r.impactados.length}. Ya estaban: ${r.yaImpactados.length}. Rechazados: ${r.rechazados.length}.`,
        ),
        ...r.rechazados.map((x) =>
          mensaje(
            'error',
            `Rechazado — dni ${x.dni}, ${x.fecha}, ${x.tipoImpacto}: ${x.detalle ?? 'sin detalle'}`,
          ),
        ),
      ];
      // Igual que al procesar de a una: se reemplazan las filas tocadas, no
      // se rehace la consulta del día.
      const actualizadas = new Map(r.jornadas.map((j) => [claveDe(j), j]));
      setJornadas((previas) => previas.map((p) => actualizadas.get(claveDe(p)) ?? p));
      setSeleccion(new Set());
      setAvisos(nodos);
    } catch (err) {
      setAvisos([mensaje('error', mensajeDe(err))]);
    }
  }

  async function resolver(decision: DecisionSupervisor, comentario: string): Promise<void> {
    if (!jornadaActiva) return;
    const j = jornadaActiva;
    setResolviendo(true);
    try {
      await resolverJornada(j.dni, j.fechaOperativa, decision, comentario);
      await buscar({ conservarAvisos: true });
      setAvisos([mensaje('ok', `Resuelta: dni ${j.dni}, ${aDdMmAaaa(j.fechaOperativa)}.`)]);
    } catch (err) {
      setAvisos([mensaje('error', mensajeDe(err))]);
    } finally {
      setResolviendo(false);
    }
  }

  // El supervisor tiene que ver qué NO está pasando: callarlo haría creer que
  // el circuito está completo.
  const advertencias: readonly { etiqueta: string | null; texto: string }[] = [
    ...(modo === 'demo'
      ? [
          {
            etiqueta: 'Modo demo.',
            texto:
              'Los datos son ficticios y no se impacta GeoVictoria. Se activa con ASISTENCIA_MODO=demo para poder revisar la pantalla mientras siguen abiertos los bloqueos.',
          },
        ]
      : []),
    ...(modulo?.bloqueos ?? []).map((texto) => ({ etiqueta: null, texto })),
  ];

  return (
    <div className="asistencia">
      {advertencias.length > 0 && (
        <Aviso tono="atencion">
          {advertencias.map((linea) => (
            <p key={linea.texto} className="linea-bloqueo">
              {linea.etiqueta !== null && <strong>{`${linea.etiqueta} `}</strong>}
              {linea.texto}
            </p>
          ))}
        </Aviso>
      )}

      {/*
        El usuario y el botón de salir viven en la barra del portal, no acá:
        desde el 15-09 hay tres solapas principales y esto solo se veía dentro
        de Asistencia.
      */}
      {/*
        Encabezado y secciones en una sola línea. El trabajo del día (jornadas)
        y la consulta de lo ya hecho (histórico) son dos tareas distintas:
        tenerlas en la misma pantalla obligaba a pasar de largo una grilla de
        300 filas para llegar al histórico.
      */}
      <header className="asistencia-encabezado">
        <div>
          <h1>Asistencia al viaje</h1>
          {/* La bajada dice qué se está mirando: sale del rango consultado, no
              de una consulta nueva. */}
          <p className="bajada">{`Cruce GeoVictoria × Cristal · ${rangoMostrado}`}</p>
        </div>

        <nav className="subsolapas" aria-label="Secciones de Asistencia al viaje">
          <button
            type="button"
            className={`subsolapa${solapa === 'jornadas' ? ' activa' : ''}`}
            {...(solapa === 'jornadas' ? { 'aria-current': 'page' as const } : {})}
            onClick={() => irA('jornadas')}
          >
            <ClipboardList size={15} aria-hidden="true" />
            Jornadas
          </button>
          <button
            type="button"
            className={`subsolapa${solapa === 'historico' ? ' activa' : ''}`}
            {...(solapa === 'historico' ? { 'aria-current': 'page' as const } : {})}
            onClick={() => irA('historico')}
          >
            <History size={15} aria-hidden="true" />
            Histórico de operaciones
          </button>
        </nav>
      </header>

      {/*
        Cartel de espera de la consulta de jornadas. Vive fuera del bloque de
        solapas para que siga a la vista aunque la pantalla se redibuje.
      */}
      <ModalCargando
        abierto={buscando}
        titulo="Consultando jornadas"
        detalle="Se cruzan las marcaciones de GeoVictoria con los viajes de los TMS."
        onCancelar={cancelarBusqueda}
      />

      {/*
        Las sub-solapas se ocultan, no se desmontan: volver a Jornadas después
        de mirar el histórico tiene que mostrar la misma consulta, no dispararla
        de nuevo. Cada una se monta la primera vez que se la abre.
      */}
      {visitadas.has('historico') && (
        <div hidden={solapa !== 'historico'}>
          <Historico />
        </div>
      )}

      <div hidden={solapa !== 'jornadas'}>
      {/* §11: la barra de filtros es una sola banda horizontal, ahora como
          tarjeta y con las acciones agrupadas a la derecha. */}
      <form
        className="banda banda--filtros"
        onSubmit={(ev) => {
          ev.preventDefault();
          void buscar();
        }}
      >
        <CampoFecha etiqueta="Desde" required value={fechaDesde} onChange={setFechaDesde} />
        <CampoFecha etiqueta="Hasta" required value={fechaHasta} onChange={setFechaHasta} />
        <label className="campo">
          Dni
          <input
            type="text"
            inputMode="numeric"
            placeholder="todos"
            value={dni}
            onChange={(ev) => setDni(ev.target.value)}
          />
        </label>
        <label className="campo">
          Estado
          <select value={estado} onChange={(ev) => setEstado(ev.target.value)}>
            {ESTADOS.map(([valor, etiqueta]) => (
              <option key={valor} value={valor}>
                {etiqueta}
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          Empresa
          <select value={empresa} onChange={(ev) => setEmpresa(ev.target.value)}>
            <option value="">Todas</option>
            {empresas.map((e) => (
              <option key={e} value={e}>
                {e.toUpperCase()}
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          Grupo
          <select value={grupo} onChange={(ev) => setGrupo(ev.target.value)}>
            <option value="">Todos</option>
            {grupos.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        </label>
        <div className="acciones">
          <button type="submit" disabled={buscando}>
            {buscando ? (
              <Loader2 className="girando" size={16} aria-hidden="true" />
            ) : (
              <Search size={16} aria-hidden="true" />
            )}
            {buscando ? 'Buscando…' : 'Buscar'}
          </button>
          <button
            type="button"
            className="plano"
            onClick={() => {
              setEstado('');
              void buscar({ soloPendientes: true });
            }}
          >
            <ListFilter size={14} aria-hidden="true" />
            Solo pendientes de impactar
          </button>
          {/*
            Descarga lo que está en pantalla. Se deshabilita con la grilla
            vacía: una planilla sin filas solo genera la duda de si falló.
          */}
          <button
            type="button"
            className="plano"
            disabled={descargando || buscando || jornadas.length === 0}
            onClick={() => void descargar()}
          >
            {descargando ? (
              <Loader2 className="girando" size={14} aria-hidden="true" />
            ) : (
              <Download size={14} aria-hidden="true" />
            )}
            {descargando ? 'Generando…' : 'Descargar Excel'}
          </button>
        </div>
      </form>

      <Avisos mensajes={avisos} />

      {seleccion.size > 0 && (
        <div className="banda banda--seleccion">
          <span>
            {`${seleccion.size === 1 ? '1 jornada seleccionada' : `${String(seleccion.size)} jornadas seleccionadas`} · se ${operaciones === 1 ? 'va' : 'van'} a registrar ${String(operaciones)} ${operaciones === 1 ? 'operación' : 'operaciones'} en GeoVictoria`}
          </span>
          <button type="button" onClick={() => setDialogoAbierto(true)}>
            Procesar la selección
          </button>
        </div>
      )}

      {/* `onProcesar` solo abre la confirmación: escribir en GeoVictoria no
          puede quedar a un clic de distancia. */}
      <TablaJornadas
        jornadas={jornadas}
        seleccion={seleccion}
        activa={activa}
        procesando={procesando}
        onAlternar={alternar}
        onAlternarTodas={alternarTodas}
        onAbrirDetalle={(j) => setActiva(claveDe(j))}
        onProcesar={(j) => setAConfirmar(j)}
      />

      {jornadaActiva && (
        <DetalleJornada
          jornada={jornadaActiva}
          resolviendo={resolviendo}
          onCerrar={() => setActiva(null)}
          onResolver={(d, c) => void resolver(d, c)}
          onProcesar={(j) => {
            // Se cierra el detalle antes de pedir la confirmación: dos modales
            // abiertos a la vez dejan sin saber cuál está pidiendo qué.
            setActiva(null);
            setAConfirmar(j);
          }}
        />
      )}

      </div>

      <DialogoProcesar
        jornada={aConfirmar}
        procesando={procesando !== null}
        resultado={resultadoProceso}
        onCancelar={() => setAConfirmar(null)}
        onConfirmar={() => {
          if (aConfirmar) void procesar(aConfirmar);
        }}
        onCerrar={() => {
          setAConfirmar(null);
          setResultadoProceso(null);
        }}
      />

      <DialogoMasivo
        abierto={dialogoAbierto}
        jornadas={seleccionadas}
        onCancelar={() => setDialogoAbierto(false)}
        onConfirmar={() => void confirmarMasivo()}
      />
    </div>
  );
}
